/* eslint-disable react/prop-types */
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useState } from 'react';
// components
import SmallLoading from '../../../Components/element/SmallLoading';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { useDebouncedCallback } from 'use-debounce';
import { observer } from 'mobx-react-lite';
import InputDate from 'Components/forms/components/InputDate';
import { CloseIcon } from 'shared/icons';
import { Dialog, Flex, Box, TextField, IconButton, Text, Heading, Button } from '@radix-ui/themes';
import { useApplyJobCtx } from '../apply-job-provider';
import { FormField } from 'shared/components';
import { GoogleAnalyticsService } from 'core/services/GoogleAnalyticsService';

const ApplyJobInfoFormView = ({
   onCheckUserSuccess,
   editInfo = false,
   subscribeAlert
}) => {
   const applyJobStore = useApplyJobCtx();
   const { t } = useTranslation('global');
   const registerOrLoginStore = applyJobStore.registerOrLogin;

   const debouncedCities = useDebouncedCallback((q) => {
      registerOrLoginStore.searchCityStore.getCities({ query: q });
   }, 500);

   const handleFocus = (e) => {
      const { value } = e.target;

      if (value) return;

      registerOrLoginStore.form.controls['contact'].setValue('+38');
   };

   const handleBlur = (e) => {
      const { value } = e.target;
      const updatedValue = value === '+38' ? '' : value;
      registerOrLoginStore.form.controls['contact'].setValue(updatedValue);
   };

   return (
      <>
         <Flex mb="5" width="100%" justify="between">
            <Box>
               <Heading as="h2" mb="1" weight="medium">
                  {t('jobDescription.enterInfo')}:
               </Heading>
               <Text color="indigo" highContrast size="4">
                  {t('jobDescription.enterInfo1.1')}
               </Text>
            </Box>
            <Dialog.Close>
               <IconButton size="1" color="blue" highContrast variant="soft" radius="full">
                  <CloseIcon />
               </IconButton>
            </Dialog.Close>
         </Flex>
         <form
            onSubmit={(e) => {
               e.preventDefault();
               registerOrLoginStore.form.validate();
               if (registerOrLoginStore.form.valid) {
                  registerOrLoginStore.checkUser({
                     onSuccess: (msg) => {
                        GoogleAnalyticsService.trackEvent('begin_registration', {
                           SUCCESS: msg,
                        });
                        onCheckUserSuccess?.(msg);
                     },
                     onError: () => {
                        GoogleAnalyticsService.trackEvent('begin_registration', {
                           ERROR: t('jobDescription.checkUserError'),
                        });
                     },
                  });
               } else {
                  GoogleAnalyticsService.trackEvent('begin_registration', {
                     WARNING: 'Validation failed',
                  });
               }
            }}
         >
            <Box mb="3">
               <FormField
                  label={t('jobDescription.firstName')}
                  labelHighContrast
                  htmlFor="first_name"
                  weight="medium"
                  required
                  error={registerOrLoginStore.form.controls['first_name'].error}
               >
                  <TextField.Root
                     size="3"
                     color="blue"
                     type="text"
                     id="first_name"
                     name="first_name"
                     placeholder={t('jobDescription.firstNamePlaceholder')}
                     value={registerOrLoginStore.form.controls['first_name'].value}
                     onChange={(e) => {
                        registerOrLoginStore.form.controls['first_name'].setValue(e.target.value);
                     }}
                  ></TextField.Root>
               </FormField>
            </Box>

            <Box mb="3">
               <FormField
                  label={t('jobDescription.lastName')}
                  labelHighContrast
                  htmlFor="last_name"
                  weight="medium"
                  required
                  error={registerOrLoginStore.form.controls['last_name'].error}
               >
                  <TextField.Root
                     size="3"
                     color="blue"
                     type="text"
                     id="last_name"
                     name="last_name"
                     placeholder={t('jobDescription.lastNamePlaceholder')}
                     value={registerOrLoginStore.form.controls['last_name'].value}
                     onChange={(e) => {
                        registerOrLoginStore.form.controls['last_name'].setValue(e.target.value);
                     }}
                  ></TextField.Root>
               </FormField>
            </Box>

            <Box mb="3">
               <FormField
                  label={t('jobDescription.email')}
                  labelHighContrast
                  htmlFor="email_address"
                  weight="medium"
                  error={registerOrLoginStore.form.controls['email_address'].error}
               >
                  <TextField.Root
                     size="3"
                     color="blue"
                     type="text"
                     id="email_address"
                     name="email_address"
                     placeholder={t('jobDescription.emailPlaceholder')}
                     value={registerOrLoginStore.form.controls['email_address'].value}
                     onChange={(e) => {
                        registerOrLoginStore.form.controls['email_address'].setValue(
                           e.target.value
                        );
                     }}
                  ></TextField.Root>
               </FormField>
            </Box>

            <Box mb="3">
               <FormField
                  label={t('jobDescription.contact')}
                  labelHighContrast
                  htmlFor="contact"
                  weight="medium"
                  error={
                     registerOrLoginStore.form.controls['contact'].error ||
                     registerOrLoginStore.form.error?.contact
                  }
               >
                  <TextField.Root
                     size="3"
                     color="blue"
                     type="text"
                     id="contact"
                     name="contact"
                     placeholder={t('jobDescription.contactPlaceholder')}
                     value={registerOrLoginStore.form.controls['contact'].value}
                     onChange={(e) => {
                        registerOrLoginStore.form.controls['contact'].setValue(e.target.value);
                     }}
                     onFocus={handleFocus}
                     onBlur={handleBlur}
                  ></TextField.Root>
               </FormField>
            </Box>

            <Box mb="3">
               <FormField
                  label="Місто"
                  labelHighContrast
                  htmlFor="city_id"
                  required
                  weight="medium"
                  error={registerOrLoginStore.form.controls['city_id'].error}
               >
                  <AutocompleteV2.Root
                     placeholder="Оберіть місто"
                     selectedKey={registerOrLoginStore.form.controls['city_id'].value || null}
                     items={registerOrLoginStore.searchCityStore.cities}
                     loading={registerOrLoginStore.searchCityStore.citiesLoading}
                     getOptionValue={(city) => city.id}
                     getOptionLabel={(city) => city.name}
                     onSelect={(loc) => {
                        registerOrLoginStore.searchCityStore.setSelected(loc);
                        registerOrLoginStore.form.controls['city_id'].setValue(loc.id);
                     }}
                     onSearch={(v) => {
                        registerOrLoginStore.form.controls['city_id'].setValue('');
                        debouncedCities(v);
                     }}
                  ></AutocompleteV2.Root>
               </FormField>
            </Box>

            <Box mb="4">
               <FormField
                  label="Дата народження"
                  labelHighContrast
                  htmlFor="birthday"
                  required
                  weight="medium"
                  error={registerOrLoginStore.form.controls['birthday'].error}
               >
                  <InputDate
                     name="birthday"
                     minDate="1930-01-01"
                     value={registerOrLoginStore.form.controls['birthday'].value}
                     handleChange={(e) => {
                        registerOrLoginStore.form.controls['birthday'].setValue(e.target.value);
                     }}
                     maxDate={new Date().toISOString().split('T')[0]}
                  />
               </FormField>
            </Box>

            <div className="mb-4">
               {editInfo ? (
                  <>
                     <div className="subscribeAlertBox">
                        <label className="subscribeAlertLabel">
                           <input
                              type="checkbox"
                              name="subscribeAlert"
                              checked={subscribeAlert}
                              className="me-2 subscribeAlertCheckbox"
                              onChange={() => setSubscribeAlert((prevState) => !prevState)}
                           />
                           {t('jobDescription.subscribeAlert')}
                        </label>
                     </div>
                     <div className="dataProcessingConsentBox">
                        <p>
                           {t('jobDescription.bottomTxt1')}{' '}
                           <Link to="/data-processing-consent">
                              {t('jobDescription.bottomTxt2')}
                           </Link>
                        </p>
                     </div>
                  </>
               ) : (
                  <p className="">
                     {t('employerRegister.bottomTxt1')}{' '}
                     <Link to="/terms-of-use">{t('employerRegister.bottomTxt2')}</Link>
                     {', '}
                     {t('employerRegister.bottomTxt7')}{' '}
                     <Link to="/offer-agreement">{t('employerRegister.bottomTxt8')}</Link>{' '}
                     {t('employerRegister.bottomTxt5')}{' '}
                     <Link to="/data-processing-consent">{t('employerRegister.bottomTxt6')}</Link>
                  </p>
               )}
            </div>
            <div className="d-flex justify-content-evenly">
               {editInfo && (
                  <button
                     type="button"
                     className="btn button2"
                     onClick={() => setStep('questionnaire')}
                  >
                     {t('jobDescription.backButton')}
                  </button>
               )}
               {editInfo ? (
                  <button
                     type="submit"
                     onClick={() => {
                        // registerOrLoginStore.form.validate()
                     }}
                     disabled={isSubmitting}
                     className="mt-0 btn button1"
                  >
                     {t('jobDescription.applyNow')}{' '}
                     {isSubmitting && <SmallLoading width="16px" height="16px" />}
                  </button>
               ) : (
                  <Button
                     type="submit"
                     size="4"
                     radius="small"
                     loading={registerOrLoginStore.checkUserProccessing}
                  >
                     {t('jobDescription.continueButton')}
                  </Button>
               )}
            </div>
         </form>
      </>
   );
};
const ApplyJobInfoForm = observer(ApplyJobInfoFormView);
export default ApplyJobInfoForm;
